import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import axios from "axios";
import { Download, Mail, RotateCcw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const FREQUENCY_LABELS = {
  250: "250",
  500: "500",
  1000: "1k",
  2000: "2k",
  4000: "4k",
  8000: "8k",
};

// Hearing loss classification thresholds
const CLASSIFICATIONS = {
  NORMAL: { max: 25, label: "Normal", color: "#34D399" },
  MILD: { max: 40, label: "Mild", color: "#FBBF24" },
  MODERATE: { max: 55, label: "Moderate", color: "#FB923C" },
  SEVERE: { max: 100, label: "Severe", color: "#F87171" },
};

// Chart configuration constants (extracted to avoid inline object creation)
const CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 20 };
const Y_AXIS_TICKS = [0, 25, 40, 55, 70, 85, 100];

function classifyHearing(leftEar, rightEar) {
  const allLevels = [...leftEar, ...rightEar].map((r) => r.db_level);
  const worstLevel = Math.max(...allLevels);

  if (worstLevel <= CLASSIFICATIONS.NORMAL.max) return CLASSIFICATIONS.NORMAL;
  if (worstLevel <= CLASSIFICATIONS.MILD.max) return CLASSIFICATIONS.MILD;
  if (worstLevel <= CLASSIFICATIONS.MODERATE.max) return CLASSIFICATIONS.MODERATE;
  return CLASSIFICATIONS.SEVERE;
}

// Extracted component for the audiogram chart
function AudiogramChart({ chartData }) {
  return (
    <div className="bg-[#0A0F1C] rounded-xl p-4">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1E2D3D" />
          <XAxis
            dataKey="frequency"
            stroke="#64748B"
            tick={{ fill: "#94A3B8", fontSize: 12 }}
            axisLine={{ stroke: "#1E2D3D" }}
            label={{
              value: "Frequency (Hz)",
              position: "bottom",
              fill: "#64748B",
              fontSize: 12,
            }}
          />
          <YAxis
            domain={[0, 100]}
            reversed={true}
            stroke="#64748B"
            tick={{ fill: "#94A3B8", fontSize: 12 }}
            axisLine={{ stroke: "#1E2D3D" }}
            label={{
              value: "Hearing Level (dB)",
              angle: -90,
              position: "insideLeft",
              fill: "#64748B",
              fontSize: 12,
            }}
            ticks={Y_AXIS_TICKS}
          />
          <ReferenceLine y={25} stroke="#34D399" strokeDasharray="5 5" />
          <ReferenceLine y={40} stroke="#FBBF24" strokeDasharray="5 5" />
          <ReferenceLine y={55} stroke="#FB923C" strokeDasharray="5 5" />
          <Legend
            wrapperStyle={{ paddingTop: 20 }}
            formatter={(value) => (
              <span style={{ color: "#94A3B8" }}>{value}</span>
            )}
          />
          <Line
            type="monotone"
            dataKey="leftEar"
            name="Left Ear"
            stroke="#22D3EE"
            strokeWidth={3}
            dot={{ fill: "#22D3EE", strokeWidth: 2, r: 6 }}
            activeDot={{ r: 8 }}
          />
          <Line
            type="monotone"
            dataKey="rightEar"
            name="Right Ear"
            stroke="#60A5FA"
            strokeWidth={3}
            dot={{ fill: "#60A5FA", strokeWidth: 2, r: 6 }}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Extracted component for results table
function ResultsTable({ chartData }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#1E2D3D]">
            <th className="py-3 px-4 text-left text-[#64748B] text-sm font-medium">
              Frequency
            </th>
            <th className="py-3 px-4 text-center text-[#22D3EE] text-sm font-medium">
              Left Ear
            </th>
            <th className="py-3 px-4 text-center text-[#60A5FA] text-sm font-medium">
              Right Ear
            </th>
          </tr>
        </thead>
        <tbody>
          {chartData.map((row) => (
            <tr key={row.frequencyFull} className="border-b border-[#1E2D3D]/50">
              <td className="py-3 px-4 text-[#F8FAFC]">{row.frequencyFull}</td>
              <td className="py-3 px-4 text-center text-[#22D3EE] font-medium">
                {row.leftEar} dB
              </td>
              <td className="py-3 px-4 text-center text-[#60A5FA] font-medium">
                {row.rightEar} dB
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Extracted component for classification legend
function ClassificationLegend() {
  return (
    <div className="bg-[#1E293B] rounded-xl p-4">
      <h4 className="text-[#F8FAFC] font-medium mb-3">Understanding Your Results</h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.values(CLASSIFICATIONS).map((cls) => (
          <div key={cls.label} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: cls.color }}
            />
            <span className="text-[#94A3B8] text-sm">
              {cls.label} (0-{cls.max} dB)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Custom hook for handling download functionality
function useDownload(resultsRef, userName) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!resultsRef.current) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(resultsRef.current, {
        backgroundColor: "#0A0F1C",
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: resultsRef.current.scrollWidth,
        windowHeight: resultsRef.current.scrollHeight,
      });

      const dataUrl = canvas.toDataURL("image/png");
      const fileName = `hearing-test-results-${userName?.replace(/\s+/g, "-") || "user"}.png`;
      
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      if (isMobile) {
        if (isIOS) {
          // iOS Safari: Open image in new tab with data URL directly
          const newWindow = window.open("", "_blank");
          if (newWindow) {
            // Use DOM methods instead of document.write to avoid XSS vulnerability
            const doc = newWindow.document;
            doc.documentElement.innerHTML = "";
            
            const head = doc.createElement("head");
            const title = doc.createElement("title");
            title.textContent = "Your Hearing Test Results";
            head.appendChild(title);
            
            const meta = doc.createElement("meta");
            meta.name = "viewport";
            meta.content = "width=device-width, initial-scale=1";
            head.appendChild(meta);
            
            const style = doc.createElement("style");
            style.textContent = `
              body { margin: 0; padding: 20px; background: #0A0F1C; text-align: center; }
              img { max-width: 100%; height: auto; border-radius: 8px; }
              p { color: #94A3B8; font-family: Arial, sans-serif; margin-top: 20px; }
            `;
            head.appendChild(style);
            doc.documentElement.appendChild(head);
            
            const body = doc.createElement("body");
            const img = doc.createElement("img");
            img.src = dataUrl;
            img.alt = "Hearing Test Results";
            body.appendChild(img);
            
            const p = doc.createElement("p");
            p.textContent = "Press and hold the image to save it to your device.";
            body.appendChild(p);
            doc.documentElement.appendChild(body);
          }
          toast.success("Image opened - press and hold to save!");
        } else {
          // Android: Try blob download
          try {
            const response = await fetch(dataUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("Results downloaded successfully!");
          } catch (downloadError) {
            // Fallback: open in new tab
            window.open(dataUrl, "_blank");
            toast.success("Image opened - tap and hold to save!");
          }
        }
      } else {
        // Desktop: Standard download
        const link = document.createElement("a");
        link.download = fileName;
        link.href = dataUrl;
        link.click();
        toast.success("Results downloaded successfully!");
      }
    } catch (error) {
      toast.error("Failed to download results. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [resultsRef, userName]);

  return { downloading, handleDownload };
}

// Custom hook for handling email functionality
function useEmailResults(leadData, leftEarResults, rightEarResults, classificationLabel) {
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleEmailResults = useCallback(async () => {
    if (!leadData?.email) {
      toast.error("No email address available");
      return;
    }

    setEmailSending(true);
    try {
      await axios.post(`${API}/email-results`, {
        email: leadData.email,
        name: leadData.name,
        left_ear: leftEarResults,
        right_ear: rightEarResults,
        classification: classificationLabel,
      });

      setEmailSent(true);
      toast.success(`Results sent to ${leadData.email}`);
    } catch (error) {
      toast.error("Failed to send email. Please try again.");
    } finally {
      setEmailSending(false);
    }
  }, [leadData, leftEarResults, rightEarResults, classificationLabel]);

  return { emailSending, emailSent, handleEmailResults };
}

export default function Results({
  leadData,
  leftEarResults,
  rightEarResults,
  healthFlags,
  onRetake,
}) {
  const resultsRef = useRef(null);

  const classification = useMemo(
    () => classifyHearing(leftEarResults, rightEarResults),
    [leftEarResults, rightEarResults]
  );

  // Prepare chart data with memoization
  const chartData = useMemo(
    () =>
      leftEarResults.map((left, index) => ({
        frequency: FREQUENCY_LABELS[left.frequency],
        frequencyFull: `${left.frequency} Hz`,
        leftEar: left.db_level,
        rightEar: rightEarResults[index].db_level,
      })),
    [leftEarResults, rightEarResults]
  );

  // Custom hooks for download and email
  const { downloading, handleDownload } = useDownload(resultsRef, leadData?.name);
  const { emailSending, emailSent, handleEmailResults } = useEmailResults(
    leadData,
    leftEarResults,
    rightEarResults,
    classification.label
  );

  // Save results to database on mount
  useEffect(() => {
    const saveResults = async () => {
      if (!leadData?.id) return;
      
      try {
        await axios.post(`${API}/results`, {
          lead_id: leadData.id,
          name: leadData.name,
          email: leadData.email,
          left_ear: leftEarResults,
          right_ear: rightEarResults,
          classification: classification.label,
          health_flags: healthFlags,
        });
      } catch (error) {
        // Error is logged server-side, silently continue for user
      }
    };

    saveResults();
  }, [leadData, leftEarResults, rightEarResults, classification.label, healthFlags]);

  const formattedDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Downloadable Results Card */}
      <div
        ref={resultsRef}
        className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 mb-6"
        data-testid="results-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <img 
            src={LOGO_URL} 
            alt="BuyHearingAid.com" 
            className="h-8 sm:h-10"
          />
          <p className="text-[#64748B] text-sm">{formattedDate}</p>
        </div>

        {/* User Info */}
        {leadData && (
          <div className="mb-6">
            <p className="text-[#94A3B8] text-sm">Results for</p>
            <p className="text-[#F8FAFC] text-xl font-medium font-['Outfit']">
              {leadData.name}
            </p>
          </div>
        )}

        {/* Classification Badge */}
        <div
          className="text-center py-6 px-4 rounded-xl mb-8"
          style={{ backgroundColor: `${classification.color}10` }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2">
            Overall Classification
          </p>
          <h2
            className="text-3xl sm:text-4xl font-medium font-['Outfit']"
            style={{ color: classification.color }}
            data-testid="classification-result"
          >
            {classification.label} Hearing
          </h2>
        </div>

        {/* Audiogram Chart */}
        <div className="mb-8">
          <h3 className="text-[#F8FAFC] text-lg font-medium mb-4 font-['Outfit']">
            Your Audiogram
          </h3>
          <AudiogramChart chartData={chartData} />
        </div>

        {/* Results Table */}
        <div className="mb-8">
          <h3 className="text-[#F8FAFC] text-lg font-medium mb-4 font-['Outfit']">
            Detailed Results
          </h3>
          <ResultsTable chartData={chartData} />
        </div>

        {/* Understanding Results */}
        <ClassificationLegend />

        {/* Disclaimer */}
        <p className="text-[#64748B] text-xs text-center mt-6">
          This online hearing test is for educational purposes only and does not
          replace a professional evaluation by an audiologist.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="py-4 px-6 bg-[#111827] border border-[#334155] text-[#F8FAFC] font-semibold rounded-xl hover:bg-[#1E293B] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          data-testid="download-results-btn"
        >
          {downloading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Downloading...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Download Results
            </>
          )}
        </button>

        <button
          onClick={handleEmailResults}
          disabled={emailSending || emailSent}
          className={`py-4 px-6 font-semibold rounded-xl transition-all flex items-center justify-center gap-3 ${
            emailSent
              ? "bg-[#34D399]/20 border border-[#34D399] text-[#34D399]"
              : "bg-[#22D3EE] text-[#0A0F1C] hover:bg-[#67E8F9]"
          } disabled:opacity-50`}
          data-testid="email-results-btn"
        >
          {emailSending ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </>
          ) : emailSent ? (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Email Sent!
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" />
              Email Results
            </>
          )}
        </button>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#22D3EE]/10 to-[#60A5FA]/10 border border-[#22D3EE]/30 rounded-2xl p-6 sm:p-8 mb-6">
        <div className="text-center">
          <h3 className="text-xl sm:text-2xl font-medium text-[#F8FAFC] mb-3 font-['Outfit']">
            Ready to Explore Hearing Aid Options?
          </h3>
          <p className="text-[#94A3B8] mb-6 max-w-lg mx-auto">
            Our experts can help you find the perfect hearing aid based on your results.
            Get personalized recommendations today.
          </p>
          <a
            href="https://buyhearingaid.com/pages/contact-us"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 py-4 px-8 bg-[#22D3EE] text-[#0A0F1C] font-semibold rounded-xl hover:bg-[#67E8F9] transition-all"
            data-testid="contact-cta-btn"
          >
            Contact Us
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </div>

      {/* Retake Test */}
      <button
        onClick={onRetake}
        className="w-full py-4 px-6 border border-[#334155] text-[#94A3B8] font-medium rounded-xl hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors flex items-center justify-center gap-2"
        data-testid="retake-test-btn"
      >
        <RotateCcw className="w-5 h-5" />
        Take the Test Again
      </button>
    </div>
  );
}
